import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout.jsx';
import { api } from '../api.js';

const fmt = (n) => '$ ' + new Intl.NumberFormat('es-CO').format(n ?? 0);

export default function Cierre() {
  const { id } = useParams();
  const turnoId = Number(id);
  const navigate = useNavigate();

  const [turno, setTurno] = useState(null);
  const [productos, setProductos] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [movs, setMovs] = useState([]);
  const [conteos, setConteos] = useState({});
  const [unidadFiltro, setUnidadFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const abierto = await api.turnoAbierto();
        setTurno(abierto.turno);
        const [p, u, m] = await Promise.all([api.productos(), api.unidades(), api.movimientos(turnoId)]);
        setProductos(p.productos);
        setUnidades(u.unidades);
        setMovs(m.movimientos);
      } catch (err) {
        setError('No se pudo cargar el turno: ' + err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [turnoId]);

  const updConteo = (key, valor) => setConteos((c) => ({ ...c, [key]: valor }));

  const consumoPorProducto = (productoId, unidadId) => {
    return movs
      .filter((m) => {
        const pid = m.producto_id ?? null;
        const uid = m.unidad_id ?? null;
        const esProducto = (pid === productoId) || (m.producto && productos.find((p) => p.id === productoId)?.nombre === m.producto);
        const esUnidad = !unidadId ? true : uid === unidadId;
        return esProducto && esUnidad;
      })
      .reduce((acc, m) => {
        if (m.tipo === 'venta' || m.tipo === 'cortesia' || m.tipo === 'merma') return acc + Number(m.cantidad ?? 0);
        if (m.tipo === 'reposicion') return acc - Number(m.cantidad ?? 0);
        return acc;
      }, 0);
  };

  const filas = productos.map((p) => {
    const consumido = movs
      .filter((m) => m.producto && m.producto === p.nombre && (!unidadFiltro || m.unidad === unidades.find((u) => u.id === Number(unidadFiltro))?.codigo))
      .reduce((acc, m) => {
        if (m.tipo === 'venta' || m.tipo === 'cortesia' || m.tipo === 'merma') return acc + Number(m.cantidad ?? 0);
        if (m.tipo === 'reposicion') return acc - Number(m.cantidad ?? 0);
        return acc;
      }, 0);
    return { ...p, consumido };
  });

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const payload = filas
        .filter((f) => conteos[f.id] !== undefined && conteos[f.id] !== '')
        .map((f) => ({
          producto_id: f.id,
          unidad_id: unidadFiltro ? Number(unidadFiltro) : null,
          fisico: Number(conteos[f.id]),
        }));
      const r = await api.cerrarTurno(turnoId, payload);
      setResultado(r);
    } catch (err) {
      setError('Error al cerrar: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Layout><p className="text-muted">Cargando turno…</p></Layout>;

  if (resultado) {
    return (
      <Layout>
        <div className="max-w-xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mb-2">Turno cerrado</div>
          <h1 className="text-4xl font-bold tracking-tight mb-4">{resultado.estado === 'cerrado' ? 'Cuadre limpio.' : 'Turno cerrado con descuadre.'}</h1>
          <div className="card mb-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mb-2">Descuadre total</div>
            <div className="text-3xl font-semibold tracking-tight">{fmt(resultado.descuadre)}</div>
            <div className="font-mono text-[11px] text-muted mt-2">Estado: {resultado.estado}</div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => navigate(`/reportes`)} className="btn-primary">Ir a reportes</button>
            <button onClick={() => navigate('/')} className="btn-secondary">Volver al inicio</button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mb-2">Cierre de turno</div>
        <h1 className="text-4xl font-bold tracking-tight">Conteo físico.</h1>
        <p className="text-muted mt-2">Cuenta lo que hay físicamente en cada producto. El sistema calcula el descuadre.</p>
      </div>

      <div className="card mb-4">
        <label className="label">Filtro por unidad (opcional)</label>
        <select className="input max-w-xs" value={unidadFiltro} onChange={(e) => setUnidadFiltro(e.target.value)}>
          <option value="">— global / oficina —</option>
          {unidades.map((u) => (
            <option key={u.id} value={u.id}>{u.codigo} · {u.nombre}</option>
          ))}
        </select>
      </div>

      <div className="card overflow-x-auto mb-6">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="text-left">
              <th className="label pb-3">Producto</th>
              <th className="label pb-3 text-right">Consumido turno</th>
              <th className="label pb-3 text-right">Conteo físico</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f) => (
              <tr key={f.id} className="border-t border-line">
                <td className="py-3">
                  <div className="font-medium tracking-tight">{f.nombre}</div>
                  {f.sku && <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted mt-0.5">{f.sku}</div>}
                </td>
                <td className="py-3 text-right font-mono text-sm text-muted">{f.consumido} ud</td>
                <td className="py-3 text-right">
                  <input
                    className="input text-right max-w-[110px] inline-block"
                    type="number"
                    min={0}
                    value={conteos[f.id] ?? ''}
                    onChange={(e) => updConteo(f.id, e.target.value)}
                    placeholder="—"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error && <div className="text-sm text-red-700 font-mono mb-4">{error}</div>}

      <div className="flex gap-3">
        <button onClick={submit} className="btn-accent" disabled={submitting}>
          {submitting ? 'Cerrando…' : 'Cerrar turno'}
        </button>
        <button onClick={() => navigate('/')} className="btn-secondary">Cancelar</button>
      </div>
    </Layout>
  );
}
